import { truckFrames } from './truckFrames.generated';

/*
 * Scrollgesteuerte LKW-Bildsequenz.
 *
 * Der Fortschritt wird ausschließlich aus der Lage der per `position: sticky`
 * fixierten Bühne innerhalb ihrer Bahn abgeleitet. Das Skript scrollt nicht
 * selbst und unterdrückt keine Scroll-Geste: Firefox/Android scrollt auf dem
 * Compositor-Thread, jede Korrektur per `scrollTo` käme einen Frame zu spät
 * und wäre als Sprung sichtbar.
 *
 * Gezeichnet wird in ein <canvas>. Ein Wechsel von `img.src` würde in Firefox
 * kurzzeitig die intrinsische Bildgröße verlieren und damit bei jedem Frame
 * das Layout umbrechen.
 */

// Auf Mobilgeräten wird nur jeder n-te Frame verwendet — halbe Datenmenge und
// halbe Decode-Last. Auf 1 setzen, um die volle Sequenz zu erzwingen.
const MOBILE_FRAME_STRIDE = 2;
// Gleichzeitig laufende Bild-Requests. Alle Frames auf einmal anzustoßen
// blockiert auf dem Handy Netz und Decoder genau dann, wenn die Sequenz
// beginnen soll.
const PRELOAD_CONCURRENCY = 6;
// Abstand zum Viewport, ab dem vorgeladen wird.
const PRELOAD_ROOT_MARGIN = '150% 0px';
// Backing-Store nie über diesen Faktor hinaus vergrößern.
const MAX_PIXEL_RATIO = 2;

const MOBILE_QUERY = '(max-width: 768px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

interface SequenceFrame {
	url: string;
	image: HTMLImageElement | null;
	ready: boolean;
}

function frameUrl(index: number): string {
	const asset = truckFrames[index] as unknown;
	return typeof asset === 'string' ? asset : (asset as { default: string }).default;
}

export function initTruckScrollSequence(): void {
	const track = document.querySelector<HTMLElement>('[data-truck-sequence]');
	const stage = track?.querySelector<HTMLElement>('[data-truck-stage]');
	const canvas = track?.querySelector<HTMLCanvasElement>('[data-truck-canvas]');

	if (!track || !stage || !canvas) return;

	const context = canvas.getContext('2d', { alpha: false });
	if (!context) return;

	const mobileQuery = window.matchMedia(MOBILE_QUERY);
	const reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);

	let frames: SequenceFrame[] = [];
	let sourceWidth = 0;
	let renderedIndex = -1;
	let renderScheduled = false;
	let preloading = false;
	// Kennzeichnet den aktuellen Ladedurchlauf. Nach einem Neuaufbau dürfen
	// noch laufende Ladeketten des alten Durchlaufs nicht weiterarbeiten.
	let preloadToken = 0;
	let inView = false;

	const isActive = () => !reducedMotionQuery.matches;

	const buildFrames = (): SequenceFrame[] => {
		const stride = mobileQuery.matches ? MOBILE_FRAME_STRIDE : 1;
		const urls: string[] = [];

		for (let index = 0; index < truckFrames.length; index += stride) urls.push(frameUrl(index));

		// Der Schlussframe muss erhalten bleiben, sonst endet die Sequenz je
		// nach Schrittweite vor der letzten Position.
		const lastUrl = frameUrl(truckFrames.length - 1);
		if (urls[urls.length - 1] !== lastUrl) urls.push(lastUrl);

		return urls.map((url) => ({ url, image: null, ready: false }));
	};

	// Backing-Store an die tatsächliche Anzeigegröße anpassen. Gibt zurück, ob
	// sich etwas geändert hat — ein Resize leert die Canvas, danach muss neu
	// gezeichnet werden.
	const resizeCanvas = (): boolean => {
		const rect = canvas.getBoundingClientRect();
		if (rect.width === 0 || rect.height === 0) return false;

		const ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
		const limit = sourceWidth || Number.POSITIVE_INFINITY;
		const width = Math.max(1, Math.min(Math.round(rect.width * ratio), limit));
		const height = Math.max(1, Math.round(width * (rect.height / rect.width)));

		if (canvas.width === width && canvas.height === height) return false;

		canvas.width = width;
		canvas.height = height;
		return true;
	};

	// Solange ein Frame noch lädt, den nächstgelegenen bereits dekodierten
	// zeichnen. Das ist deutlich ruhiger als eine Lücke in der Sequenz.
	const drawableFrame = (index: number): SequenceFrame | null => {
		for (let offset = 0; offset < frames.length; offset += 1) {
			const earlier = frames[index - offset];
			if (earlier && earlier.ready) return earlier;

			const later = frames[index + offset];
			if (later && later.ready) return later;
		}

		return null;
	};

	const draw = (index: number): void => {
		const exact = frames[index];
		const frame = exact && exact.ready ? exact : drawableFrame(index);
		if (!frame || !frame.image) return;

		context.drawImage(frame.image, 0, 0, canvas.width, canvas.height);
		track.classList.add('is-playing');
	};

	// Fortschritt = Weg, den die Bühne innerhalb ihrer Bahn bereits
	// zurückgelegt hat. Beide Rects stammen aus demselben Frame, das Ergebnis
	// ist damit unabhängig von `window.scrollY` und von der Frage, wann der
	// Browser das Scroll-Event zustellt.
	const currentIndex = (): number => {
		const trackRect = track.getBoundingClientRect();
		const stageRect = stage.getBoundingClientRect();
		const travel = trackRect.height - stageRect.height;
		if (travel <= 0) return 0;

		const progress = Math.min(Math.max((stageRect.top - trackRect.top) / travel, 0), 1);
		return Math.round(progress * (frames.length - 1));
	};

	const render = (): void => {
		if (!isActive() || frames.length === 0) return;

		const index = currentIndex();
		const resized = resizeCanvas();
		if (index === renderedIndex && !resized) return;

		renderedIndex = index;
		draw(index);
	};

	const requestRender = (): void => {
		if (renderScheduled) return;

		renderScheduled = true;
		window.requestAnimationFrame(() => {
			renderScheduled = false;
			render();
		});
	};

	const forceRender = (): void => {
		renderedIndex = -1;
		requestRender();
	};

	const startPreload = (): void => {
		if (preloading || !isActive() || frames.length === 0) return;
		preloading = true;

		const token = preloadToken;
		let nextIndex = 0;

		const loadNext = (): void => {
			if (token !== preloadToken) return;

			const index = nextIndex;
			nextIndex += 1;

			const frame = frames[index];
			if (!frame) return;

			const image = new Image();
			image.decoding = 'async';
			frame.image = image;

			const settle = (): void => {
				if (token !== preloadToken) return;

				frame.ready = true;
				if (sourceWidth === 0) sourceWidth = image.naturalWidth;
				forceRender();
				loadNext();
			};

			image.onload = () => {
				// Erst nach dem Dekodieren nachladen: so bleibt auch die
				// Decoder-Last begrenzt, nicht nur die Zahl der Requests.
				if (typeof image.decode === 'function') image.decode().then(settle, settle);
				else settle();
			};
			image.onerror = () => {
				frame.image = null;
				loadNext();
			};

			image.src = frame.url;
		};

		for (let slot = 0; slot < Math.min(PRELOAD_CONCURRENCY, frames.length); slot += 1) loadNext();
	};

	const applyMode = (): void => {
		track.dataset.truckMode = isActive() ? 'scroll' : 'static';
	};

	// Neuaufbau nach Breakpoint- oder Motion-Wechsel. Die Canvas behält dabei
	// bewusst ihren letzten Inhalt, bis der erste neue Frame dekodiert ist.
	const reset = (): void => {
		frames = buildFrames();
		preloadToken += 1;
		preloading = false;
		renderedIndex = -1;
		if (inView) startPreload();
		requestRender();
	};

	applyMode();
	reset();

	if ('IntersectionObserver' in window) {
		const observer = new IntersectionObserver((entries) => {
			for (const entry of entries) {
				inView = entry.isIntersecting;
				if (inView) startPreload();
			}
		}, { rootMargin: PRELOAD_ROOT_MARGIN });

		observer.observe(track);
	} else {
		inView = true;
		startPreload();
	}

	window.addEventListener('scroll', requestRender, { passive: true });
	window.addEventListener('resize', forceRender, { passive: true });
	window.addEventListener('orientationchange', forceRender, { passive: true });

	if ('ResizeObserver' in window) new ResizeObserver(forceRender).observe(stage);

	const onMediaChange = (): void => {
		applyMode();
		reset();
	};

	mobileQuery.addEventListener('change', onMediaChange);
	reducedMotionQuery.addEventListener('change', onMediaChange);
}
